/**
 * Lumo Techniques Service
 * 
 * Manages proprietary Lumo objection handling techniques:
 * - Mirror & Pivot, Shrug Close, Two-Minute Drill, etc.
 * - Technique recommendations based on objection/persona
 * - Mastery tracking and progression
 * - Industry-specific variations (SaaS, Retail, Healthcare, Finance)
 */

import { supabase } from '../lib/supabase';

const lumoTechniquesService = {
  /**
   * Get all available Lumo techniques
   */
  async getAllTechniques() {
    try {
      const { data, error } = await supabase
        .from('lumo_techniques')
        .select('*')
        .order('difficulty_level', { ascending: true })
        .order('avg_success_rate', { ascending: false });

      if (error) throw error;

      return data?.map(technique => ({
        id: technique.id,
        name: technique.technique_name,
        slug: technique.technique_slug,
        tagline: technique.tagline,
        category: technique.category,
        step1: technique.step_1,
        step2: technique.step_2,
        step3: technique.step_3,
        exampleSetup: technique.example_setup,
        exampleExecution: technique.example_execution,
        bestForObjections: technique.best_for_objections,
        bestForPersonas: technique.best_for_personas,
        timingNote: technique.timing_note,
        difficultyLevel: technique.difficulty_level,
        avgSuccessRate: technique.avg_success_rate,
      })) || [];
    } catch (error) {
      console.error('Error fetching Lumo techniques:', error);
      throw error;
    }
  },

  /**
   * Get technique by slug
   */
  async getTechniqueBySlug(slug) {
    try {
      const { data, error } = await supabase
        .from('lumo_techniques')
        .select('*')
        .eq('technique_slug', slug)
        .single();

      if (error) throw error;

      if (!data) return null;

      return {
        id: data.id,
        name: data.technique_name,
        slug: data.technique_slug,
        tagline: data.tagline,
        category: data.category,
        step1: data.step_1,
        step2: data.step_2,
        step3: data.step_3,
        exampleSetup: data.example_setup,
        exampleExecution: data.example_execution,
        bestForObjections: data.best_for_objections,
        bestForPersonas: data.best_for_personas,
        timingNote: data.timing_note,
        difficultyLevel: data.difficulty_level,
        avgSuccessRate: data.avg_success_rate,
      };
    } catch (error) {
      console.error('Error fetching technique by slug:', error);
      throw error;
    }
  },

  /**
   * Get recommended techniques for an objection
   */
  async getRecommendedTechniques(objectionType, buyerPersona = null, userSkillLevel = 'intermediate') {
    try {
      const { data, error } = await supabase
        .rpc('get_recommended_technique', {
          p_objection_type: objectionType,
          p_buyer_persona: buyerPersona,
          p_user_skill_level: userSkillLevel
        });

      if (error) throw error;

      return data?.map(technique => ({
        id: technique.technique_id,
        name: technique.technique_name,
        slug: technique.technique_slug,
        tagline: technique.tagline,
        exampleExecution: technique.example_execution,
        difficultyLevel: technique.difficulty_level,
        successRate: technique.success_rate,
      })) || [];
    } catch (error) {
      console.error('Error getting recommended techniques:', error);
      throw error;
    }
  },

  /**
   * Track technique application during practice
   */
  async trackTechniqueApplication({
    userId,
    techniqueId,
    sessionId = null,
    sessionType = 'daily_drill',
    objectionFaced,
    appliedCorrectly = false,
    executionScore = 0.0,
    timeToExecute = null,
    outcome = 'failed',
    whatWorked = '',
    whatNeedsWork = ''
  }) {
    try {
      const { data, error } = await supabase
        .from('technique_applications')
        .insert({
          user_id: userId,
          technique_id: techniqueId,
          session_id: sessionId,
          session_type: sessionType,
          objection_faced: objectionFaced,
          technique_applied_correctly: appliedCorrectly,
          execution_score: executionScore,
          time_to_execute: timeToExecute,
          outcome: outcome,
          what_worked: whatWorked,
          what_needs_work: whatNeedsWork
        })
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        userId: data.user_id,
        techniqueId: data.technique_id,
        sessionId: data.session_id,
        outcome: data.outcome,
        executionScore: data.execution_score,
        createdAt: data.created_at
      };
    } catch (error) {
      console.error('Error tracking technique application:', error);
      throw error;
    }
  },

  /**
   * Get user's technique mastery levels
   */
  async getUserTechniqueMastery(userId) {
    try {
      const { data, error } = await supabase
        .rpc('get_user_technique_mastery', {
          p_user_id: userId
        });

      if (error) throw error;

      return data?.map(mastery => ({
        techniqueName: mastery.technique_name,
        timesAttempted: mastery.times_attempted,
        timesSucceeded: mastery.times_succeeded,
        successRate: mastery.success_rate,
        avgExecutionScore: mastery.avg_execution_score,
        masteryLevel: mastery.mastery_level
      })) || [];
    } catch (error) {
      console.error('Error fetching technique mastery:', error);
      throw error;
    }
  },

  /**
   * Get technique applications for a user (history)
   */
  async getTechniqueApplications(userId, limit = 20) {
    try {
      const { data, error } = await supabase
        .from('technique_applications')
        .select(`
          *,
          lumo_techniques (
            technique_name,
            technique_slug,
            category
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data?.map(app => ({
        id: app.id,
        techniqueName: app.lumo_techniques?.technique_name,
        techniqueSlug: app.lumo_techniques?.technique_slug,
        category: app.lumo_techniques?.category,
        objectionFaced: app.objection_faced,
        sessionType: app.session_type,
        appliedCorrectly: app.technique_applied_correctly,
        executionScore: app.execution_score,
        timeToExecute: app.time_to_execute,
        outcome: app.outcome,
        whatWorked: app.what_worked,
        whatNeedsWork: app.what_needs_work,
        createdAt: app.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching technique applications:', error);
      throw error;
    }
  },

  /**
   * Analyze user's response to detect which technique they used
   */
  detectTechniqueUsed(userResponse, objectionFaced) {
    const response = userResponse.toLowerCase();
    const objection = objectionFaced.toLowerCase();

    // Mirror & Pivot detection
    if (response.includes(objection.split(' ').slice(0, 3).join(' ')) && response.includes('?')) {
      return {
        techniqueSlug: 'mirror-pivot',
        confidence: 0.8,
        evidence: 'Response mirrors objection and pivots with question'
      };
    }

    // Shrug Close detection
    if (response.match(/if.*not a fit|no worries|no problem/i) && response.match(/but|however|though/i)) {
      return {
        techniqueSlug: 'shrug-close',
        confidence: 0.75,
        evidence: 'Response shows detachment with "no worries" plus compelling reason'
      };
    }

    // Human Reset detection
    if (response.match(/let's step back|let me ask|what's the real/i)) {
      return {
        techniqueSlug: 'human-reset',
        confidence: 0.85,
        evidence: 'Response uses reset phrase to reframe conversation'
      };
    }

    // Frame Flip detection
    if (response.match(/that's actually why|that's exactly why|perfect reason/i)) {
      return {
        techniqueSlug: 'frame-flip',
        confidence: 0.8,
        evidence: 'Response reframes objection as reason to buy'
      };
    }

    // Exit Ramp detection
    if (response.match(/happy to step back|pause and revisit|not a priority/i)) {
      return {
        techniqueSlug: 'exit-ramp',
        confidence: 0.9,
        evidence: 'Response offers clean exit to create urgency'
      };
    }

    return {
      techniqueSlug: null,
      confidence: 0.0,
      evidence: 'No clear technique detected'
    };
  },

  /**
   * Score technique execution quality
   */
  scoreTechniqueExecution(technique, userResponse, metrics) {
    let score = 0.5; // Base score

    const response = userResponse.toLowerCase();
    const wordCount = userResponse.split(' ').length;
    const questionCount = (userResponse.match(/\?/g) || []).length;

    switch (technique.slug) {
      case 'mirror-pivot':
        // Good: mirrors objection + asks question + under 30 words
        if (questionCount >= 1) score += 0.2;
        if (wordCount <= 30) score += 0.2;
        if (wordCount <= 20) score += 0.1;
        break;

      case 'shrug-close':
        // Good: detachment phrase + compelling metric + under 40 words
        if (response.match(/no worries|not a fit/i)) score += 0.2;
        if (response.match(/\d+%|\d+ days|\d+x/)) score += 0.2; // Has metric
        if (wordCount <= 40) score += 0.1;
        break;

      case 'two-minute-drill':
        // Good: handles in under 120 seconds + asks question + has next step
        if (metrics?.timeToExecute && metrics.timeToExecute <= 120) score += 0.3;
        if (questionCount >= 1) score += 0.1;
        if (response.match(/next step|worth a|let's/i)) score += 0.1;
        break;

      case 'human-reset':
        // Good: reset phrase + diagnostic question + under 20 words
        if (response.match(/step back|let me ask|what's the real/i)) score += 0.3;
        if (questionCount >= 1) score += 0.1;
        if (wordCount <= 20) score += 0.1;
        break;

      case 'frame-flip':
        // Good: reframe phrase + specific example + metric
        if (response.match(/that's why|exactly why|perfect reason/i)) score += 0.2;
        if (response.match(/\d+ days|\d+ customers|fastest/i)) score += 0.2;
        if (wordCount <= 35) score += 0.1;
        break;

      case 'exit-ramp':
        // Good: exit phrase + detached tone + watches for reaction
        if (response.match(/step back|pause|revisit|not a priority/i)) score += 0.3;
        if (wordCount <= 30) score += 0.1;
        if (!response.match(/please|really|definitely/i)) score += 0.1; // Not needy
        break;

      default:
        score = 0.5;
    }

    return Math.min(score, 1.0); // Cap at 1.0
  },

  /**
   * Get technique with industry-specific variation
   */
  async getTechniqueForIndustry(techniqueSlug, industry = 'universal') {
    try {
      const { data, error } = await supabase
        .rpc('get_technique_for_industry', {
          p_technique_slug: techniqueSlug,
          p_industry: industry
        });

      if (error) throw error;

      if (!data || data.length === 0) return null;

      const result = data[0];
      const baseTechnique = result.base_technique;
      const industryVariation = result.industry_variation;

      // Merge base technique with industry variation
      return {
        id: baseTechnique.id,
        name: baseTechnique.technique_name,
        slug: baseTechnique.technique_slug,
        tagline: baseTechnique.tagline,
        category: baseTechnique.category,
        step1: baseTechnique.step_1,
        step2: baseTechnique.step_2,
        step3: baseTechnique.step_3,
        exampleSetup: industryVariation?.industry_example_setup || baseTechnique.example_setup,
        exampleExecution: industryVariation?.industry_example_execution || baseTechnique.example_execution,
        bestForObjections: baseTechnique.best_for_objections,
        bestForPersonas: baseTechnique.best_for_personas,
        timingNote: baseTechnique.timing_note,
        difficultyLevel: baseTechnique.difficulty_level,
        avgSuccessRate: baseTechnique.avg_success_rate,
        // Industry-specific fields
        industry: industry,
        industryObjections: industryVariation?.common_objections || [],
        industryNote: industryVariation?.industry_specific_note || '',
        industryMetrics: industryVariation?.typical_metrics || [],
        industryTerminology: industryVariation?.industry_terminology || []
      };
    } catch (error) {
      console.error('Error fetching technique for industry:', error);
      throw error;
    }
  },

  /**
   * Get all industry variations for a technique
   */
  async getIndustryVariations(techniqueId) {
    try {
      const { data, error } = await supabase
        .from('technique_industry_variations')
        .select('*')
        .eq('base_technique_id', techniqueId);

      if (error) throw error;

      return data?.map(variation => ({
        id: variation.id,
        industry: variation.industry,
        commonObjections: variation.common_objections,
        exampleSetup: variation.industry_example_setup,
        exampleExecution: variation.industry_example_execution,
        industryNote: variation.industry_specific_note,
        typicalMetrics: variation.typical_metrics,
        industryTerminology: variation.industry_terminology
      })) || [];
    } catch (error) {
      console.error('Error fetching industry variations:', error);
      throw error;
    }
  },

  /**
   * Get cascade challenge scenario for industry
   */
  async getCascadeChallenge(industry, difficultyLevel = 'intermediate') {
    try {
      const { data, error } = await supabase
        .rpc('get_cascade_challenge_for_industry', {
          p_industry: industry,
          p_difficulty: difficultyLevel
        });

      if (error) throw error;

      if (!data || data.length === 0) return null;

      const scenario = data[0];

      return {
        id: scenario.scenario_id,
        name: scenario.scenario_name,
        objection1: scenario.objections.objection_1.text,
        objection1Type: scenario.objections.objection_1.type,
        objection2: scenario.objections.objection_2.text,
        objection2Type: scenario.objections.objection_2.type,
        objection3: scenario.objections.objection_3.text,
        objection3Type: scenario.objections.objection_3.type,
        recommendedTechnique1: scenario.recommended_techniques.technique_1,
        recommendedTechnique2: scenario.recommended_techniques.technique_2,
        recommendedTechnique3: scenario.recommended_techniques.technique_3,
        buyerPersona: scenario.context.buyer_persona,
        scenarioContext: scenario.context.scenario_context,
        successCriteria: scenario.context.success_criteria,
        coachingTip: scenario.context.coaching_tip
      };
    } catch (error) {
      console.error('Error fetching cascade challenge:', error);
      throw error;
    }
  },

  /**
   * Get all cascade challenges for an industry
   */
  async getAllCascadeChallenges(industry = null) {
    try {
      let query = supabase
        .from('cascade_challenge_scenarios')
        .select('*')
        .order('difficulty_level', { ascending: true });

      if (industry) {
        query = query.eq('industry', industry);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(scenario => ({
        id: scenario.id,
        name: scenario.scenario_name,
        industry: scenario.industry,
        difficultyLevel: scenario.difficulty_level,
        objection1: scenario.objection_1,
        objection1Type: scenario.objection_1_type,
        objection2: scenario.objection_2,
        objection2Type: scenario.objection_2_type,
        objection3: scenario.objection_3,
        objection3Type: scenario.objection_3_type,
        recommendedTechnique1: scenario.recommended_technique_1,
        recommendedTechnique2: scenario.recommended_technique_2,
        recommendedTechnique3: scenario.recommended_technique_3,
        buyerPersona: scenario.buyer_persona,
        scenarioContext: scenario.scenario_context,
        successCriteria: scenario.success_criteria,
        coachingTip: scenario.coaching_tip
      })) || [];
    } catch (error) {
      console.error('Error fetching cascade challenges:', error);
      throw error;
    }
  }
};

export default lumoTechniquesService;
